import apiClient, { getApiErrorMessage } from './apiClient';
import { getAuthToken } from '../utils/storage';
import { buildPreviewFiles, extractSandboxSessionId } from '../utils/previewFiles';

const defaultPreviewHtml = (title, description) =>
  `<div style="font-family:Tahoma,sans-serif;padding:40px;background:#f8f5ef;color:#123629"><h1>${title}</h1><p>${description || 'Start building here.'}</p></div>`;

const mapVisibilityToApi = (visibility) => (visibility === 'public' ? 'public' : 'private');
const mapVisibilityFromApi = (visibility) => {
  const value = String(visibility ?? '').trim().toLowerCase();
  return value === 'public' ? 'public' : 'your';
};

const unwrapApiData = (data) => data?.data || data?.Data || data;
const unwrapApiList = (data) => {
  const unwrapped = unwrapApiData(data);

  if (Array.isArray(unwrapped)) {
    return unwrapped;
  }

  if (Array.isArray(unwrapped?.items)) {
    return unwrapped.items;
  }

  if (Array.isArray(unwrapped?.Items)) {
    return unwrapped.Items;
  }

  if (unwrapped && typeof unwrapped === 'object') {
    return [unwrapped];
  }

  return [];
};

const getProjectFiles = (project) => project?.Files || project?.files || null;
const getProjectTitle = (project) => project?.Title || project?.title || 'Untitled Project';
const getProjectDescription = (project) => project?.Description || project?.description || '';

const normalizeCategory = (category) => ({
  id: category?.Id || category?.id || '',
  name: category?.Name || category?.name || ''
});

const normalizeAttachment = (attachment) => ({
  id: attachment?.Id || attachment?.id || '',
  fileName: attachment?.FileName || attachment?.fileName || '',
  contentType: attachment?.ContentType || attachment?.contentType || '',
  sizeBytes: attachment?.SizeBytes ?? attachment?.sizeBytes ?? 0,
  url: attachment?.Url || attachment?.url || '',
  createdAt: attachment?.CreatedAt || attachment?.createdAt || null
});

const normalizeFeature = (feature) => ({
  key: feature?.Key || feature?.key || '',
  title: feature?.Title || feature?.title || feature?.Key || feature?.key || '',
  description: feature?.Description || feature?.description || null,
  metadata: feature?.Metadata || feature?.metadata || {}
});

export const normalizeProject = (project) => {
  const coverImageUrl =
    project?.CoverImageUrl || project?.coverImageUrl || project?.CoverImage || project?.coverImage || '';

  return {
  id: project?.Id || project?.id || `project-${Date.now()}`,
  name: getProjectTitle(project),
  title: getProjectTitle(project),
  description: getProjectDescription(project),
  visibility: mapVisibilityFromApi(project?.Visibility || project?.visibility),
  files: getProjectFiles(project),
  previewUrl: project?.PreviewUrl || project?.previewUrl || '',
  previewSessionId: extractSandboxSessionId(project),
  previewExpiresAtUtc: project?.PreviewExpiresAtUtc || project?.previewExpiresAtUtc || null,
  ownerId: project?.OwnerId || project?.ownerId || null,
  createdAt: project?.CreatedAt || project?.createdAt || new Date().toISOString(),
  updatedAt: project?.UpdatedAt || project?.updatedAt || new Date().toISOString(),
  coverImageUrl,
  coverImage: coverImageUrl || null,
  sortOrder: project?.SortOrder ?? project?.sortOrder ?? 0,
  viewCount: project?.ViewCount ?? project?.viewCount ?? null,
  categories: (project?.Categories || project?.categories || []).map(normalizeCategory).filter((item) => item.id),
  attachments: (project?.Attachments || project?.attachments || []).map(normalizeAttachment),
  features: (project?.Features || project?.features || []).map(normalizeFeature).filter((item) => item.key),
  previewHtml:
    project?.previewHtml ??
    defaultPreviewHtml(
      getProjectTitle(project),
      getProjectDescription(project)
    ),
  previewFiles:
    project?.previewFiles ||
    buildPreviewFiles({
      files: getProjectFiles(project),
      html:
        project?.previewHtml ??
        defaultPreviewHtml(
          getProjectTitle(project),
          getProjectDescription(project)
        )
    })
  };
};

export const fetchProjectsApi = async () => {
  if (!getAuthToken()) {
    return [];
  }

  try {
    const { data } = await apiClient.get('/Projects');
    return unwrapApiList(data).map(normalizeProject);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Project list request failed.'));
  }
};

export const createProjectApi = async (payload) => {
  if (!getAuthToken()) {
    throw new Error('Login is required to create a project.');
  }

  try {
    const { data } = await apiClient.post('/Projects', {
      Title: payload.name,
      Description: payload.description,
      Visibility: mapVisibilityToApi(payload.visibility),
      ...(Array.isArray(payload.features) && payload.features.length > 0 ? { Features: payload.features } : {})
    });

    return normalizeProject(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Create project failed.'));
  }
};

export const fetchProjectByIdApi = async (projectId) => {
  try {
    const { data } = await apiClient.get(`/Projects/${projectId}`);
    return normalizeProject(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Project request failed.'));
  }
};

export const updateProjectApi = async (payload) => {
  if (!getAuthToken()) {
    throw new Error('Login is required to update a project.');
  }

  try {
    const formData = new FormData();
    formData.append('Title', payload.name);
    formData.append('Description', payload.description || '');
    formData.append('Visibility', mapVisibilityToApi(payload.visibility));

    if (payload.sortOrder !== undefined && payload.sortOrder !== null && payload.sortOrder !== '') {
      formData.append('SortOrder', String(payload.sortOrder));
    }

    (payload.categoryIds || []).forEach((categoryId) => {
      if (categoryId) {
        formData.append('CategoryIds', categoryId);
      }
    });

    if (payload.coverImageFile instanceof File) {
      formData.append('CoverImage', payload.coverImageFile);
    }

    const { data } = await apiClient.put(`/Projects/${encodeURIComponent(payload.id)}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return normalizeProject(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Update project failed.'));
  }
};

export const saveProjectFilesApi = async ({ projectId, files }) => {
  if (!getAuthToken()) {
    throw new Error('Login is required to update project files.');
  }

  try {
    const { data } = await apiClient.patch(`/Projects/${projectId}/files`, {
      Files: files
    });

    const normalizedResponse = unwrapApiData(data);

    if (
      normalizedResponse &&
      (
        normalizedResponse.Id ||
        normalizedResponse.id ||
        normalizedResponse.Title ||
        normalizedResponse.title ||
        normalizedResponse.Description ||
        normalizedResponse.description ||
        normalizedResponse.PreviewUrl ||
        normalizedResponse.previewUrl ||
        normalizedResponse.Visibility ||
        normalizedResponse.visibility
      )
    ) {
      return {
        ...normalizeProject(normalizedResponse),
        id: normalizedResponse.Id || normalizedResponse.id || projectId,
        files
      };
    }

    return {
      id: projectId,
      files,
      previewFiles: buildPreviewFiles({ files })
    };
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Update project files failed.'));
  }
};

export const cloneProjectApi = async (projectId) => {
  if (!getAuthToken()) {
    throw new Error('Login is required to clone a project.');
  }

  try {
    const { data } = await apiClient.post(`/Projects/${encodeURIComponent(projectId)}/clone`);
    return normalizeProject(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Clone project failed.'));
  }
};

export const deleteProjectApi = async (projectId) => {
  if (!getAuthToken()) {
    return projectId;
  }

  try {
    await apiClient.delete(`/Projects/${projectId}`);
    return projectId;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Delete project failed.'));
  }
};
